import * as React from 'react';
import './EntityList.scss';
import { ListGroup, ListGroupItem } from 'reactstrap';
import EntityItem from './EntityItem';
import { Entity, EntitiesData, EntityType } from '../../store/entities/state';
import TitledContainer from '../TitledContainer';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { useEntitiesPageSizeSetting } from '../../store/settings/hooks';
import { useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import Add from '@material-ui/icons/Add';
import { useCallback, useMemo, useRef, useState } from 'react';
import { entityOpened } from '../../store/entities/actions';
import { useCookies } from 'react-cookie';
import CollapsableItem from '../CollapsableItem';
import AppButton from '../AppButton';

export interface EntityItemTab {
    id: string,
    title: string,
    render: (isOpen: boolean, entity: Entity, onClose: () => void) => React.ReactElement,
}

export interface EntityListStateProps {
    id: string,
    title: string,
    entityType: EntityType,
    entitiesData: EntitiesData<Entity>,
    itemTabs: EntityItemTab[],
    searchOptions?: string[],
    searchOptionLabelResolver?: (option: string) => string,
    searchTextResolver?: (option: string) => string,
    searchMatcher?: (inputValue: string, option: string) => boolean,
    canCreateEntities: EntityType[],
    advancedOptions?: React.ReactElement,
    advancedTitle?: string,
    advancedIsOpen?: boolean,
    onAdvancedIsOpenChange?: (isOpen: boolean) => void,
    headerActions?: React.ReactNode,
    renderItemHeader: (entity: Entity) => React.ReactElement,
    preloadHistory?: boolean,
    itemFilter?: (entity: Entity) => boolean,
}

export interface EntityListDispatchProps {
    onLoad: (keyword?: string, offset?: number, ids?: string[]) => void,
    onCreateEntity: (type: EntityType) => void,
    onSearchOptionSelected?: (option: string) => void,
}

export type EntityListProps = EntityListStateProps & EntityListDispatchProps;

const EntityList: React.FC<EntityListProps> = (props) => {

    const {
        id,
        title,
        entityType,
        entitiesData,
        itemTabs,
        searchOptions,
        searchOptionLabelResolver,
        searchTextResolver,
        searchMatcher,
        canCreateEntities,
        advancedOptions,
        advancedTitle,
        advancedIsOpen,
        onAdvancedIsOpenChange,
        headerActions,
        renderItemHeader,
        preloadHistory,
        itemFilter,
        onLoad,
        onCreateEntity,
        onSearchOptionSelected,
    } = props;

    const location = useLocation();
    const dispatch = useDispatch();
    const t = useTranslations();

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const entityOpen = useMemo(() => searchParams.get('id') || undefined, [searchParams]);
    const activeTab = useMemo(() => searchParams.get('tab') ?? '', [searchParams]);

    const cookieName = `appointmint-${id}-history`
    const [cookies, setCookie] = useCookies([cookieName]);
    const [_, setOpenHistory] = useState<string[]>(cookies[cookieName] ?? [])

    const elementOpen = useRef<HTMLDivElement>(null)
    const setEntityOpen = useCallback((entityId: string|undefined) => {
        const params = new URLSearchParams(location.search);

        if (entityId) params.set('id', entityId);
        else params.delete('id');

        if(entityId && preloadHistory) {
            setOpenHistory(prev => {
                const history = [entityId, ...prev.filter((c: string) => c !== entityId)].slice(0, 10)
                setCookie(cookieName, history)
                return history
            })
        }
        dispatch(push({...location, hash: '', search: params.toString() ? `?${params.toString()}` : ''}));
    }, [dispatch, setOpenHistory, location, setCookie, cookieName])

    const handleTabChange = useCallback((tabId: string) => {
        const params = new URLSearchParams(location.search);
        if (tabId) params.set('tab', tabId);
        else params.delete('tab');
        dispatch(push({...location, search: `?${params.toString()}`}));
    }, [dispatch, location]);

    const pageSize = useEntitiesPageSizeSetting();

    // eslint-disable-next-line
    React.useEffect(() => {
        onLoad(undefined, undefined, entityOpen ? [entityOpen] : [])
    }, []);

    React.useEffect(() => {
        if (!entityOpen) return;
        dispatch(entityOpened(entityType, entityOpen));
        if (!entitiesData.searchResult.includes(entityOpen)) {
            onLoad(undefined, undefined, [entityOpen]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityOpen])

    const [count, setCount] = React.useState(0);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [hasMoreToLoad, setHasMoreToLoad] = React.useState<boolean>((entitiesData.searchResult.length && (entitiesData.searchResult.length % pageSize === 0)) || false);
    const [searchTerm, setSearchTerm] = React.useState<string | undefined>(undefined);
    const [showHistory, setShowHistory] = React.useState(preloadHistory === true);

    React.useEffect(() => {
        if (!searchTerm && entitiesData.changingHistory.length) return;

        if(isLoadingMore && entitiesData.searchResult.length === count) {
            setHasMoreToLoad(false);
        }
        else {
            setHasMoreToLoad((entitiesData.searchResult.length && (entitiesData.searchResult.length % pageSize === 0)) || false);
        }
        setIsLoadingMore(false);
        setCount(entitiesData.searchResult.length);
        if (entitiesData.searchResult?.length) setShowHistory(false)
        // eslint-disable-next-line
    }, [entitiesData.searchResult])

    const isOpenEntityLoaded = useMemo(() => {
        if (!entityOpen) return false;
        return entitiesData.searchResult.includes(entityOpen) || entitiesData.changingHistory.includes(entityOpen);
    }, [entityOpen, entitiesData.searchResult, entitiesData.changingHistory]);

    React.useEffect(() => {
        if (!entityOpen || !isOpenEntityLoaded) return;

        const timeout = window.setTimeout(() => {
            elementOpen.current?.scrollIntoView({behavior: 'smooth'});
        }, 200);

        return () => window.clearTimeout(timeout);
    }, [entityOpen, isOpenEntityLoaded])

    React.useEffect(() => setIsLoadingMore(entitiesData.isLoading || false), [entitiesData.isLoading]);

    const handleSearch = useCallback((term?: string) =>{
        setSearchTerm(term)
        onLoad(term, 0)
        setShowHistory(false)
    }, [setSearchTerm, onLoad])

    const handleSearchOptionSelected = useCallback((option: string) => {
        setSearchTerm(undefined);
        setShowHistory(false);
        onSearchOptionSelected?.(option);
    }, [onSearchOptionSelected])

    const handleLoadMore = useCallback(() =>  onLoad(searchTerm, count), [onLoad, searchTerm, count])

    const handleOnOpen = useCallback((entity: Entity) => () => setEntityOpen(entity.id), [setEntityOpen])
    const handleOnClose = useCallback(() => setEntityOpen(undefined), [setEntityOpen])
    const handleRenderHeader = useCallback((entity: Entity) => () => renderItemHeader(entity), [renderItemHeader])

    const isOpen = useCallback((entity: Entity) => entity.id === entityOpen, [entityOpen])

    const options = useMemo(() => [
        {id: 'appointment', description: t(TK.appointment), onClick: () => onCreateEntity('appointments'), enabled: canCreateEntities.includes('appointments')},
        {id: 'client', description: t(TK.client), onClick: () => onCreateEntity('clients'), enabled: canCreateEntities.includes('clients')},
        {id: 'professional', description: t(TK.professional), onClick: () => onCreateEntity('professionals'), enabled: canCreateEntities.includes('professionals')},
        {id: 'user', description: t(TK.user), onClick: () => onCreateEntity('users'), enabled: canCreateEntities.includes('users')},
    ].filter(o => o.enabled), [t, onCreateEntity, canCreateEntities])

    const itemsToShow = useMemo(
        () => (!showHistory ? entitiesData.searchResult : entitiesData.changingHistory)
            .map(id => entitiesData.items[id])
            .filter(e => e)
            .filter(e => !itemFilter || itemFilter(e)),
        [entitiesData.searchResult, entitiesData.items, entityOpen, showHistory, itemFilter])

    const renderEntityItem = useCallback((entity: Entity) => (
        <ListGroupItem id={entity.id} key={entity.id} className={entity.id === entityOpen ? 'selectedItem' : 'item'}>
            <EntityItem
                id={`${id}-item-${entity.id}`}
                refObj={isOpen(entity) ? elementOpen : undefined}
                isOpen={isOpen(entity)}
                activeTab={isOpen(entity) ? activeTab : ''}
                onTabChange={handleTabChange}
                onOpen={handleOnOpen(entity)}
                onClose={handleOnClose}
                renderHeader={handleRenderHeader(entity)}
                tabs={itemTabs.map(tab => ({id: tab.id, title: tab.title, render: (open, onClose) => tab.render(open, entity, onClose) }))} />
        </ListGroupItem>
    ), [entityOpen, id, isOpen, activeTab, handleTabChange, handleOnOpen, handleOnClose, handleRenderHeader, itemTabs])

    const subTitle = useMemo(() => {
        if (!advancedOptions) return undefined
        return (
            <CollapsableItem
                title={advancedTitle ?? t(TK.advanced)}
                size='sm'
                style={{width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}
                isOpen={advancedIsOpen}
                onSetOpen={onAdvancedIsOpenChange}
            >
                {advancedOptions}
            </CollapsableItem>
        )
    }, [advancedOptions, advancedTitle, advancedIsOpen, onAdvancedIsOpenChange, t])

    return (
        <TitledContainer
            id={id}
            title={title}
            subTitle={subTitle}
            isLoading={entitiesData.isLoading}
            onSearch={handleSearch}
            searchOptions={searchOptions}
            searchOptionLabelResolver={searchOptionLabelResolver}
            searchTextResolver={searchTextResolver}
            searchMatcher={searchMatcher}
            onSearchOptionSelected={onSearchOptionSelected ? handleSearchOptionSelected : undefined}
            titleMargin={advancedOptions ? 'XS' : 'M'}
            extraActions={headerActions}
            actions={[{id: 'add', Icon: Add, options}]}
        >
            <ListGroup>
                { itemsToShow.map(renderEntityItem) }
                { hasMoreToLoad && <AppButton color="link" onClick={handleLoadMore}>{t(TK.loadMore)}</AppButton> }
            </ListGroup>
        </TitledContainer>
    );
}

export default EntityList;
